import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Network,
  Radar,
  Lock,
  Layers,
  Terminal,
  ArrowUpRight,
} from "lucide-react";

// Change this if you're not using @ alias
import { Eyebrow } from "../components/SectionHeader";

const SOLUTIONS = [
  {
    icon: Network,
    title: "Payment Connectivity & Orchestration",
    body:
      "Connect once to Pay10x and reach a growing network of global payment rails, mobile wallets, bank accounts, and payout methods through a single, unified API. We handle routing logic, message formatting, and network-specific requirements behind the scenes.",
  },
  {
    icon: Radar,
    title: "Real-Time Status & Settlement Visibility",
    body:
      "Every transaction you send through Pay10x comes with real-time tracking from initiation to confirmation. Get consistent, normalized status updates regardless of which underlying network processed the payment, so your reconciliation and customer communication never miss a beat.",
  },
  {
    icon: Lock,
    title: "Secure Message Infrastructure",
    body:
      "Pay10x is built around secure, auditable message exchange. Every instruction, confirmation, and status update is encrypted in transit, authenticated, and logged, giving you a transparent record of every transaction.",
  },
  {
    icon: Layers,
    title: "Multi-Network Reach",
    body:
      "Rather than integrating separately with each global payment network, integrate once with Pay10x. As new payment partners are added, your access to corridors, currencies, and payout methods grows automatically.",
  },
  {
    icon: Terminal,
    title: "Developer-First Integration",
    body:
      "Comprehensive API documentation, sandbox environments, SDKs, and dedicated technical support help you move from testing to production in weeks instead of months.",
  },
];

export default function Solutions() {
  return (
    <div>
      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-24 bg-[color:var(--p10-ivory)] border-b border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow>Solutions · What we offer</Eyebrow>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            Everything you need to move money.
            <br />
            <em className="text-[color:var(--p10-royal)]">
              Nothing you don't.
            </em>
          </motion.h1>

          <p className="mt-8 max-w-2xl text-lg text-[color:var(--p10-ink)]/70 leading-relaxed">
            Pay10x removes the burden of building and maintaining connectivity
            to global payment networks, so your team can focus on your
            customers.
          </p>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {SOLUTIONS.map((solution, index) => {
            const Icon = solution.icon;

            return (
              <motion.article
                key={solution.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 py-12 md:py-16 border-t border-[color:var(--p10-line)] first:border-t-0"
              >
                <div className="md:col-span-1">
                  <div className="font-mono-tech text-xs tracking-[0.2em] text-[color:var(--p10-ink)]/50">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                </div>

                <div className="md:col-span-4">
                  <Icon className="w-8 h-8 text-[color:var(--p10-royal)] mb-6" />

                  <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
                    {solution.title}
                  </h2>
                </div>

                <div className="md:col-span-7 md:pt-3">
                  <p className="text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                    {solution.body}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[color:var(--p10-ink)] text-white relative">
        <div className="grain"></div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Eyebrow dark>Ready to see it live?</Eyebrow>

            <h2 className="font-serif-display text-4xl md:text-5xl mt-6 leading-[1.02] max-w-2xl">
              One integration.
              <em className="text-[color:var(--p10-warm)]">
                {" "}
                Every corridor you'll ever need.
              </em>
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="solutions-cta-demo"
              className="btn-square btn-electric inline-flex items-center"
            >
              Book a Demo
              <ArrowUpRight className="ml-2 w-4 h-4" />
            </Link>

            <Link
              to="/developers"
              data-testid="solutions-cta-dev"
              className="btn-square btn-ghost-dark"
            >
              View Developers
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}